<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import MessageSendIcon from '@/assets/svg/conversations/MessageSendIcon.svg?component';

const props = withDefaults(
  defineProps<{
    proName?: string;
    // Renders the composer inert (skeleton/pending screens) without changing layout.
    disabled?: boolean;
    // Passed as a function (not an event) so the composer can await the send and
    // keep the typed text when it fails.
    onSend: (content: string) => Promise<void>;
  }>(),
  {
    proName: '',
    disabled: false,
  },
);

const message = ref('');
const isSending = ref(false);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

// Auto-grow the textarea as the message wraps (capped by max-height in the
// template, which then scrolls). Resets to a single row when cleared.
function adjustHeight(): void {
  const el = textareaRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

watch(message, () => nextTick(adjustHeight));

// Placeholder uses the candidate's first name ("Message <FirstName>").
const firstName = computed(() => props.proName.trim().split(' ')[0] ?? '');
const messagePlaceholder = computed(() =>
  firstName.value ? `Message ${firstName.value}` : 'Message',
);

const canSend = computed(
  () => !props.disabled && !isSending.value && message.value.trim().length > 0,
);

async function sendMessage(): Promise<void> {
  const trimmed = message.value.trim();
  if (!trimmed || isSending.value || props.disabled) return;

  isSending.value = true;
  try {
    await props.onSend(trimmed);
    // Clear only after a successful send so a failure keeps the typed text.
    message.value = '';
  } catch {
    // useConversationThread.sendMessage already surfaced the failure (snackbar +
    // capture); leave the text in place so the recruiter can retry.
  } finally {
    isSending.value = false;
  }
}
</script>

<template>
  <div
    class="flex items-end border-t border-tint-100 bg-tint-0 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2"
    data-test="mobile-message-composer"
  >
    <form class="flex-1" @submit.prevent="sendMessage">
      <div class="relative flex w-full items-end rounded-2xl border border-tint-100 bg-tint-0">
        <!-- No Enter-to-send: on mobile keyboards Enter inserts a newline; the
             recruiter sends explicitly via the send button. -->
        <textarea
          ref="textareaRef"
          name="mobile-message-compose"
          data-test="mobile-message-composer-textarea"
          rows="1"
          class="max-h-32 w-full resize-none overflow-y-auto rounded-2xl bg-tint-0 py-3 pl-4 pr-12 text-shade-900 outline-none placeholder:text-shade-800 disabled:bg-tint-80"
          :placeholder="messagePlaceholder"
          :aria-label="messagePlaceholder"
          :disabled="isSending || disabled"
          v-model="message"
        />

        <button
          type="submit"
          data-test="mobile-message-composer-send"
          class="absolute bottom-1 right-1 flex h-11 w-11 items-center justify-center transition-colors"
          :class="canSend ? 'cursor-pointer text-copilot' : 'cursor-not-allowed text-shade-600'"
          :disabled="!canSend"
          aria-label="Send message"
        >
          <MessageSendIcon class="text-current h-5 w-5" title="Send message" />
        </button>
      </div>
    </form>
  </div>
</template>
